import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orgNumberApi } from '../services/api';
import { isValidOrgNumber } from '../utils/orgNumber';
import {
  companyIdentifierMessages,
  sanitizeOrgNumberInput,
  sanitizeVatInput,
  withTestOrgNumber
} from '../utils/companyIdentifiers';

/**
 * Organisation and VAT number fields that behave like "Skapa hantverkare" and
 * the contractor onboarding: sanitised while typing, VAT derived from the
 * organisation number (and kept in step while it still is the derived one, so
 * correcting a mistyped number corrects it too; a VAT number entered by hand
 * stays), the organisation number checked for availability on
 * blur, and inline errors that block saving. Used by the admin's "Skapa
 * Besiktningsman" and "Skapa Jurist" (ClickUp 869fjt7ma).
 */
export function useCompanyIdentifierFields(
  organizationNumber: string,
  vatNumber: string,
  update: (patch: { organizationNumber?: string; vatNumber?: string }) => void
) {
  const { t } = useTranslation();
  const [organizationNumberTaken, setOrganizationNumberTaken] = useState(false);

  const changeOrganizationNumber = (value: string) => {
    const next = sanitizeOrgNumberInput(value);
    setOrganizationNumberTaken(false);
    update(withTestOrgNumber({ organizationNumber, vatNumber }, next));
  };

  const changeVatNumber = (value: string) => update({ vatNumber: sanitizeVatInput(value) });

  // Best-effort on blur; the API still rejects a duplicate on save (ORG_NUMBER_ALREADY_EXISTS).
  const checkOrganizationNumber = async () => {
    if (!isValidOrgNumber(organizationNumber)) return;
    try {
      const { available } = await orgNumberApi.checkAvailability(organizationNumber);
      setOrganizationNumberTaken(!available);
    } catch { /* ignore network errors on blur */ }
  };

  const messages = companyIdentifierMessages(t, { organizationNumber, vatNumber, organizationNumberTaken });
  return {
    ...messages,
    blocking: Boolean(messages.organizationNumberError || messages.vatNumberError),
    changeOrganizationNumber,
    changeVatNumber,
    checkOrganizationNumber
  };
}
